import { type FormEvent, type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { routes } from '../../routes';
import { SessionApiError } from '../../session/client';
import { orderHref } from '../orders/OrdersPage';
import { BackLink } from '../panels';
import { CounterApprovals } from './CounterApprovals';
import { RewardsCardPanel } from './RewardsCard';
import {
  HISTORY_PAGE,
  campaignTitle,
  dateOf,
  earnWays,
  historyView,
  isTestProgramme,
  spendRules,
  unitValue,
  units,
  worth,
} from './reward-model';
import {
  type Campaign,
  type Wallet,
  claimReferral,
  getOffers,
  getReferralCode,
  getWallet,
  redeemVoucher,
} from './rewards-api';
import '../orders/orders.css';
import './rewards.css';

/**
 * The Rewards page — `Just Treated Rewards` frame, signed in.
 *
 * The PENNY tab is real end to end: the balance, pending and held PENNY, how
 * you earn and spend it (the programme in force, never the frame's demo
 * rates), a PENNY code, your referral code and claiming a friend's, live PENNY
 * offers and the ledger history. Voucher cards, account credit and the swap
 * between PENNY and credit are drawn where the frame draws them, but say
 * "Coming soon" and do nothing: the platform has no credit or stamp domain
 * (owner decision D8), so there is no balance to show — not even £0.00.
 */

type Tab = 'penny' | 'vouchers' | 'credit';
const TABS: readonly Tab[] = ['penny', 'vouchers', 'credit'];

const IC = {
  star: 'M12 3.5 14.2 9l5.8.4-4.4 3.8 1.4 5.6L12 15.8l-5 3 1.4-5.6L4 9.4 9.8 9Z',
  ticket: 'M3 7h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4ZM14 7v2M14 11v2M14 15v2',
  coin: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM14.5 9.2c-.5-.8-1.4-1.2-2.5-1.2-1.5 0-2.5.8-2.5 2s1 1.6 2.5 2 2.5.8 2.5 2-1 2-2.5 2c-1.1 0-2-.4-2.5-1.2M12 6.5V8M12 16v1.5',
};

function Icon({
  d,
  stroke,
  size = 22,
}: {
  readonly d: string;
  readonly stroke: string;
  readonly size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-ord-soon">Coming soon</span>;
}

function messageOf(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

function useNarrow(): boolean {
  const read = (): boolean => (typeof window === 'undefined' ? false : window.innerWidth < 760);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = (): void => setNarrow(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function useToast(): [string, (message: string) => void] {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, flash];
}

// -- Page --------------------------------------------------------------------------------

export function RewardsPage(): JSX.Element {
  const navigator = useNavigator();
  const asked = navigator.location.query.get('tab');
  const tab: Tab = TABS.includes(asked as Tab) ? (asked as Tab) : 'penny';
  const narrow = useNarrow();
  const [toast, flash] = useToast();

  const [wallet, setWallet] = useState<{ value?: Wallet; error?: unknown; loading: boolean }>({
    loading: true,
  });
  const alive = useRef(true);
  const load = useCallback((quiet = false) => {
    if (!quiet) setWallet((previous) => ({ ...previous, loading: true }));
    getWallet()
      .then((value) => {
        if (alive.current) setWallet({ value, loading: false });
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        setWallet((previous) =>
          quiet && previous.value !== undefined ? previous : { error, loading: false },
        );
      });
  }, []);
  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const [offers, setOffers] = useState<readonly Campaign[]>([]);
  useEffect(() => {
    let live = true;
    getOffers()
      .then((campaigns) => {
        if (live) setOffers(campaigns);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const pick = (next: Tab): void => {
    navigator.replace(`${routes.rewards.path}${next === 'penny' ? '' : `?tab=${next}`}`);
  };

  const w = wallet.value;
  const pennyTile =
    w === undefined
      ? { v: wallet.loading ? '…' : '—', s: wallet.loading ? 'Loading…' : 'Not available' }
      : { v: units(w.availableUnits), s: worth(w, w.availableUnits) ?? 'Ready to use' };

  let body: ReactNode;
  if (tab === 'vouchers') {
    body = (
      <ComingSoon
        label="Voucher cards"
        head="Voucher cards"
        copy="Voucher cards aren’t on Just Treated yet. When they are, the cards you’re collecting will show here."
      />
    );
  } else if (tab === 'credit') {
    body = (
      <ComingSoon
        label="Account credit"
        head="Account credit"
        copy="Account credit and gift cards aren’t on Just Treated yet. When they are, you’ll add a code and see your credit here."
      />
    );
  } else if (w === undefined && wallet.loading) {
    body = (
      <div className="jt-ord-panel" aria-busy="true">
        <span className="jt-ord-panel__copy">Loading your PENNY…</span>
      </div>
    );
  } else if (w === undefined) {
    body = (
      <div className="jt-ord-panel" role="alert">
        <span className="jt-ord-panel__head">We couldn’t load your PENNY</span>
        <span className="jt-ord-panel__copy">{messageOf(wallet.error)}</span>
        <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={() => load()}>
          Try again
        </button>
      </div>
    );
  } else {
    body = <PennyTab wallet={w} offers={offers} onChanged={() => load(true)} flash={flash} />;
  }

  return (
    <>
      <BackLink />
      <div className="jt-rw">
        <div className="jt-site-titleblock">
          <h1 className="jt-site-h1">Rewards</h1>
          <span className="jt-site-subtitle">PENNY points</span>
        </div>

        <section aria-label="Balances" className="jt-rw-tiles">
          <BalanceTile
            on={tab === 'penny'}
            onPick={() => pick('penny')}
            k="PENNY"
            v={pennyTile.v}
            s={pennyTile.s}
            extra={
              w !== undefined && (w.pendingUnits > 0 || w.reservedUnits > 0)
                ? [
                    w.pendingUnits > 0 ? `${units(w.pendingUnits)} pending` : '',
                    w.reservedUnits > 0 ? `${units(w.reservedUnits)} held at checkout` : '',
                  ]
                    .filter((part) => part !== '')
                    .join(' · ')
                : undefined
            }
            icon={IC.star}
            ib="#fdeef0"
            ic="#c0396a"
          />
          <BalanceTile
            on={tab === 'vouchers'}
            onPick={() => pick('vouchers')}
            k="VOUCHER CARDS"
            soon
            icon={IC.ticket}
            ib="#fff1d6"
            ic="#b86e00"
          />
          <BalanceTile
            on={tab === 'credit'}
            onPick={() => pick('credit')}
            k="ACCOUNT CREDIT"
            soon
            icon={IC.coin}
            ib="#ddf2e7"
            ic="#156b49"
          />
        </section>

        <div role="tablist" aria-label="Rewards" className="jt-rw-tabs">
          {(
            [
              ['penny', 'PENNY'],
              ['vouchers', narrow ? 'Vouchers' : 'Voucher cards'],
              ['credit', narrow ? 'Credit' : 'Account credit'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`jt-rw-tab${tab === id ? ' jt-rw-tab--on' : ''}`}
              onClick={() => pick(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {body}
      </div>
      {toast === '' ? null : (
        <div className="jt-ord-toast" role="status">
          {toast}
        </div>
      )}
    </>
  );
}

function BalanceTile({
  on,
  onPick,
  k,
  v,
  s,
  extra,
  soon = false,
  icon,
  ib,
  ic,
}: {
  readonly on: boolean;
  readonly onPick: () => void;
  readonly k: string;
  readonly v?: string;
  readonly s?: string;
  readonly extra?: string;
  readonly soon?: boolean;
  readonly icon: string;
  readonly ib: string;
  readonly ic: string;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={`jt-rw-tile${on ? ' jt-rw-tile--on' : ''}`}
    >
      <span className="jt-rw-tile__text">
        <span className="jt-rw-tile__k">{k}</span>
        {soon ? (
          <>
            <span className="jt-rw-tile__soon">Coming soon</span>
            <span className="jt-rw-tile__s">Not available yet</span>
          </>
        ) : (
          <>
            <span className="jt-rw-tile__v">{v}</span>
            <span className="jt-rw-tile__s">{s}</span>
            {extra !== undefined ? <span className="jt-rw-tile__s jt-rw-tile__extra">{extra}</span> : null}
          </>
        )}
      </span>
      <span className="jt-rw-tile__icon" style={{ background: ib }} aria-hidden="true">
        <Icon d={icon} stroke={ic} />
      </span>
    </button>
  );
}

function ComingSoon({
  label,
  head,
  copy,
}: {
  readonly label: string;
  readonly head: string;
  readonly copy: string;
}): JSX.Element {
  return (
    <section aria-label={label} className="jt-rw-card" role="tabpanel">
      <span className="jt-rw-head">
        <span className="jt-rw-h2">{head}</span>
        <Soon />
      </span>
      <span className="jt-rw-copy">{copy}</span>
    </section>
  );
}

// -- PENNY --------------------------------------------------------------------------------------

function PennyTab({
  wallet,
  offers,
  onChanged,
  flash,
}: {
  readonly wallet: Wallet;
  readonly offers: readonly Campaign[];
  readonly onChanged: () => void;
  readonly flash: (message: string) => void;
}): JSX.Element {
  const programme = wallet.programme;
  const unit = programme?.unit ?? wallet.unit;
  const [shown, setShown] = useState(HISTORY_PAGE);
  const history = wallet.history.map(historyView);

  return (
    <div className="jt-rw-stack" role="tabpanel" aria-label="PENNY">
      {programme !== null && programme.name !== undefined ? (
        <p className={`jt-rw-terms${isTestProgramme(programme) ? ' jt-rw-terms--test' : ''}`} role="note">
          {isTestProgramme(programme) ? <strong>Test terms. </strong> : null}
          The programme in force is “{programme.name}”.
        </p>
      ) : null}
      {wallet.status !== 'ACTIVE' && wallet.status !== 'NONE' ? (
        <p className="jt-rw-terms jt-rw-terms--test" role="note">
          Your PENNY account is {wallet.status.toLowerCase()}.
        </p>
      ) : null}
      {wallet.expiringSoon.length > 0 ? (
        <p className="jt-rw-terms" role="note">
          {wallet.expiringSoon
            .map((lot) => `${units(lot.units)} ${unit} expires on ${dateOf(lot.expiresAt)}`)
            .join(' · ')}
        </p>
      ) : null}

      <CounterApprovals flash={flash} />
      <RewardsCardPanel flash={flash} />

      <section aria-label="PENNY points" className="jt-rw-card">
        <span className="jt-rw-head">
          <span className="jt-rw-h2">Turn PENNY into credit</span>
          <Soon />
        </span>
        <span className="jt-rw-copy">
          Swapping PENNY for account credit isn’t on Just Treated yet. Today you spend PENNY at checkout.
        </span>

        {programme === null ? (
          <div className="jt-rw-rule">
            <span className="jt-rw-h3">How you earn</span>
            <span className="jt-rw-copy">
              No PENNY programme is in force right now, so nothing is being earned. Your balance and history
              are still here.
            </span>
          </div>
        ) : (
          <>
            <div className="jt-rw-rule">
              <span className="jt-rw-h3">How you spend it</span>
              <span className="jt-rw-copy">{unitValue(programme)}.</span>
              <ul className="jt-rw-list">
                {spendRules(programme).map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
              {routes.nearby.availability === 'available' ? (
                <AppLink to={routes.nearby.path} className="jt-ord-pill jt-ord-pill--soft jt-rw-pill">
                  Shop near you
                </AppLink>
              ) : null}
            </div>
            <div className="jt-rw-rule">
              <span className="jt-rw-h3">How you earn</span>
              <div className="jt-rw-ways">
                {earnWays(programme, wallet.referral.terms).map((way) => (
                  <div key={way.t} className="jt-rw-way">
                    <span className="jt-rw-way__t">{way.t}</span>
                    <span className="jt-rw-way__d">{way.d}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <div className="jt-rw-pair">
        <VoucherForm unit={unit} onChanged={onChanged} flash={flash} />
        <Referral wallet={wallet} unit={unit} flash={flash} />
      </div>

      {offers.length > 0 ? (
        <section aria-label="PENNY offers" className="jt-rw-card">
          <span className="jt-rw-h2">PENNY offers</span>
          <div className="jt-rw-ways">
            {offers.map((offer) => (
              <div key={offer.id} className="jt-rw-way">
                <span className="jt-rw-way__t">
                  {campaignTitle(offer, unit)} · {offer.name}
                </span>
                <span className="jt-rw-way__d">
                  {[
                    offer.sponsorName !== undefined ? `With ${offer.sponsorName}` : '',
                    offer.newCustomersOnly ? 'New customers only' : '',
                    `Ends ${dateOf(offer.endsAt)}`,
                  ]
                    .filter((part) => part !== '')
                    .join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section aria-label="PENNY history" className="jt-rw-card jt-rw-hist">
        <span className="jt-rw-hist__title">History</span>
        {history.length === 0 ? (
          <div className="jt-rw-row">
            <span className="jt-rw-row__text">
              <span className="jt-rw-row__t">No PENNY activity yet</span>
              <span className="jt-rw-row__d">PENNY you earn and spend will show here.</span>
            </span>
          </div>
        ) : (
          history.slice(0, shown).map((row) => (
            <div key={row.key} className="jt-rw-row">
              <span className="jt-rw-row__text">
                <span className="jt-rw-row__t">{row.t}</span>
                <span className="jt-rw-row__d">
                  {row.d}
                  {row.orderId !== undefined ? (
                    <>
                      {' · '}
                      <AppLink to={orderHref(row.orderId)}>View order</AppLink>
                    </>
                  ) : null}
                </span>
              </span>
              <span className={`jt-rw-row__v jt-rw-row__v--${row.tone}`}>{row.v}</span>
            </div>
          ))
        )}
        {history.length > shown ? (
          <button
            type="button"
            className="jt-ord-pill jt-ord-pill--soft jt-rw-more"
            onClick={() => setShown((n) => n + HISTORY_PAGE)}
          >
            Show more ({units(history.length - shown)} more)
          </button>
        ) : null}
      </section>
    </div>
  );
}

function CodeForm({
  id,
  label,
  placeholder,
  button,
  onSubmit,
}: {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly button: string;
  /** Resolves to the success message; rejects with the server's own reason. */
  readonly onSubmit: (code: string) => Promise<string>;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | undefined>(undefined);
  const empty = code.trim() === '';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (empty || busy) return;
    setBusy(true);
    onSubmit(code.trim())
      .then((text) => {
        setMessage({ text, ok: true });
        setCode('');
      })
      .catch((error: unknown) => setMessage({ text: messageOf(error), ok: false }))
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="jt-rw-form" noValidate>
      <label htmlFor={id} className="jt-rw-label">
        {label}
      </label>
      <div className="jt-rw-form__row">
        <input
          id={id}
          value={code}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase());
            setMessage(undefined);
          }}
          placeholder={placeholder}
          autoComplete="off"
          maxLength={40}
          className={`jt-rw-input${message !== undefined && !message.ok ? ' jt-rw-input--bad' : ''}`}
          aria-invalid={message !== undefined && !message.ok}
        />
        <button
          type="submit"
          disabled={empty || busy}
          className={`jt-rw-submit${empty || busy ? ' jt-rw-submit--off' : ''}`}
        >
          {busy ? 'Checking…' : button}
        </button>
      </div>
      {message !== undefined ? (
        <span role="status" className={`jt-rw-msg${message.ok ? ' jt-rw-msg--ok' : ''}`}>
          {message.text}
        </span>
      ) : null}
    </form>
  );
}

function VoucherForm({
  unit,
  onChanged,
  flash,
}: {
  readonly unit: string;
  readonly onChanged: () => void;
  readonly flash: (message: string) => void;
}): JSX.Element {
  return (
    <section aria-label="Add a PENNY code" className="jt-rw-card">
      <span className="jt-rw-head">
        <span className="jt-rw-h2">Add a PENNY code</span>
      </span>
      <span className="jt-rw-copy">
        Got a code from Just Treated? Add it and the PENNY goes straight into your balance.
      </span>
      <CodeForm
        id="rw-voucher"
        label="Code"
        placeholder="Your PENNY code"
        button="Redeem"
        onSubmit={async (code) => {
          const { units: added } = await redeemVoucher(code);
          onChanged();
          const text = `${units(added)} ${unit} added to your balance`;
          flash(text);
          return text;
        }}
      />
    </section>
  );
}

function Referral({
  wallet,
  unit,
  flash,
}: {
  readonly wallet: Wallet;
  readonly unit: string;
  readonly flash: (message: string) => void;
}): JSX.Element {
  const [code, setCode] = useState<string | null>(wallet.referral.code);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const terms = wallet.referral.terms;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const reveal = (): void => {
    setBusy(true);
    setError(undefined);
    getReferralCode()
      .then((answer) => setCode(answer.code))
      .catch((failure: unknown) => setError(messageOf(failure)))
      .finally(() => setBusy(false));
  };

  const copy = (): void => {
    if (code === null) return;
    const done = (): void => flash('Code copied');
    const fallback = (): void => {
      const field = document.getElementById('rw-my-code');
      if (field !== null) {
        const range = document.createRange();
        range.selectNodeContents(field);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
      flash('Select the code and copy it');
    };
    if (typeof navigator.clipboard?.writeText === 'function') {
      navigator.clipboard.writeText(code).then(done, fallback);
    } else {
      fallback();
    }
  };

  const share = (): void => {
    if (code === null || !canShare) return;
    navigator
      .share({
        title: 'Just Treated',
        text: `Join me on Just Treated. Use my code ${code} when you sign up.`,
        url: window.location.origin,
      })
      .catch(() => undefined);
  };

  return (
    <section aria-label="Invite a friend" className="jt-rw-card">
      <span className="jt-rw-head">
        <span className="jt-rw-h2">Invite a friend</span>
      </span>
      <span className="jt-rw-copy">
        {terms !== null && terms.referrerUnits > 0
          ? `Share your code. You get ${units(terms.referrerUnits)} ${unit} when they complete their first order.`
          : 'Share your code with a friend who hasn’t ordered from Just Treated yet.'}
      </span>
      {code === null ? (
        <button
          type="button"
          className="jt-ord-pill jt-ord-pill--ink jt-rw-pill"
          onClick={reveal}
          disabled={busy}
        >
          {busy ? 'Getting your code…' : 'Show my code'}
        </button>
      ) : (
        <div className="jt-rw-code">
          <span id="rw-my-code" className="jt-rw-code__value" aria-label="Your referral code">
            {code}
          </span>
          <button type="button" className="jt-ord-pill jt-ord-pill--ink jt-rw-pill" onClick={copy}>
            Copy
          </button>
          {canShare ? (
            <button type="button" className="jt-ord-pill jt-ord-pill--soft jt-rw-pill" onClick={share}>
              Share
            </button>
          ) : null}
        </div>
      )}
      {error !== undefined ? (
        <span role="alert" className="jt-rw-msg">
          {error}
        </span>
      ) : null}
      <span className="jt-rw-meta">
        {units(wallet.referral.referred)} invited · {units(wallet.referral.qualified)} qualified
      </span>
      <div className="jt-rw-rule">
        <CodeForm
          id="rw-claim"
          label="Were you invited? Enter their code"
          placeholder="Their referral code"
          button="Claim"
          onSubmit={async (friend) => {
            await claimReferral(friend);
            return 'Thanks — your invite is recorded. The reward follows your first completed order.';
          }}
        />
      </div>
    </section>
  );
}
